/**
 * A progress bar with its percentage beside it.
 *
 * The number used to be printed *inside* the track, kept legible across the filled and unfilled
 * halves by `mix-blend-mode: difference`. That does not work with a dark text colour: `--fg`
 * (#1c1c1a) differenced against `--bg` (#fbfbfa) yields #DFDFE0 — near-white glyphs on a
 * near-white track, about 1.24:1. The trick only inverts if the text is white to begin with, and
 * the light theme's was not.
 *
 * It survived because `mix-blend-mode` makes a computed-style contrast check meaningless: the
 * label reports `rgb(28,28,26)` and passes, while the pixels actually drawn are #DFDFE0. Only
 * sampling the rendered image catches it, which is why no automated check ever flagged it.
 *
 * So the label sits outside the track in ordinary body text, where its contrast is whatever the
 * page's is and cannot depend on how full the bar happens to be. Three screens drew this bar with
 * copy-pasted markup; they now share one component, so the next change lands in one place.
 */
export function ProgressBar({
  percent,
  title,
  testId,
  /**
   * A word after the percentage naming what it covers.
   *
   * A goal row printed "9%" beside "0 direct" — a roll-up over the whole subtree next to a count
   * of the goal's own tasks, two scopes and no way to tell from the row that they were different
   * questions. The tooltip said so and a tooltip is not an answer on a screen you scan.
   */
  scope,
}: {
  percent: number;
  title?: string;
  testId?: string;
  scope?: string;
}) {
  return (
    <div className="progress-wrap">
      <div className="progress" title={title} data-testid={testId}>
        <div className="progress-fill" style={{ width: `${percent}%` }} />
      </div>
      <span className="progress-label">{percent}%</span>
      {/*
        A sibling of the label, not a child of it.

        Nested inside, it was part of the one span that is supposed to be a fixed-width column, so
        a parent goal's label measured 120px against a leaf's 51 and every bar on the row moved by
        the difference. The fix for *that* was to take it out of flow — which stopped it sizing the
        label and started it painting into whatever sits to the label's right. On a goal row that
        is the pace badge, and "incl. sub-goals" landed 90x19px on top of "on track · 113 days
        left" on the one goal that has both a rollup and a deadline.

        Out here it is an ordinary flex item that the row's stylesheet can put on a line of its
        own, so it neither sizes the label nor leaves the bar's column. The leading space it used
        to carry goes with it: `.progress-wrap`'s gap is the separation now.
      */}
      {scope ? <span className="progress-scope">{scope}</span> : null}
    </div>
  );
}
